import type { Maybe } from '@adornis/base/utilTypes.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { workshopRegistrationEmail } from '@chancenwerk/server/mailTemplates.js';
import { chancenWerkMailer } from '@chancenwerk/server/mailer.js';
import { WorkshopAppointment } from '@chancenwerk/workshops/entities/WorkshopAppointment.js';
import { WorkshopAttendance } from '@chancenwerk/workshops/entities/WorkshopAttendance.js';
import { DateTime } from 'luxon';
import { MailEventLog, MailEventLogType } from '../../db/eventlogs/MailEventlog.js';

function buildManagementURL(workshopID: string, accessToken: Maybe<string>) {
  if (!accessToken) {
    return null;
  }
  return `${baseConfig.get('ROOT_URL')}workshop/management/${workshopID}?accessToken=${accessToken}`;
}

export const sendWorkshopRegistrationEmail = registerQuery({
  type: () => Boolean,
  operationName: 'sendWorkshopRegistrationEmail',
  resolve: (appointmentID: string, userID: string) => {
    return async () => {
      const appointment = await WorkshopAppointment.getByID<WorkshopAppointment>(appointmentID)({
        ...WorkshopAppointment.allFields,
        workshop: {
          title: 1,
          appointmentInfo: 1,
        },
        attendance: {
          userID: 1,
        },
      });

      if (!appointment) {
        throw new Error(`Appointment with ID ${appointmentID} not found`);
      }

      const userCol = await getRawCollection<ChancenwerkUser>(ChancenwerkUser._collectionName);
      const user = await userCol.findOne({
        _id: userID,
      });

      if (!user) {
        throw new Error(`User with ID ${userID} not found`);
      }

      const attendanceID = appointment.attendance?.find(a => a.userID === user._id)?._id;
      if (!attendanceID) {
        throw new Error('User not registered for this appointment');
      }

      const attendance = await loadMongoData(WorkshopAttendance, attendanceID);
      if (!attendance) {
        throw new Error('Attendance not found');
      }

      try {
        const msg = await Message.compose({
          subject: 'CHANCENakademie: Workshop Anmeldung',
          html: workshopRegistrationEmail({
            name: `${user.vorname} ${user.nachname}`,
            workshopName: appointment.workshop?.title ?? '',
            date: appointment.start ?? DateTime.now(),
            end: appointment.end ?? DateTime.now(),
            infos: appointment.workshop?.appointmentInfo ?? '',
            managementURL: buildManagementURL(appointment.workshop?._id ?? '', attendance.accessToken),
          }),
        });
        await chancenWerkMailer.sendMail(msg, new Recipient(user.email));
        await new MailEventLog({
          userID: user._id,
          mailType: MailEventLogType.WORKSHOP_ATTENDANCE_REGISTER,
          successful: true,
          sendMailError: undefined,
          emailAddress: user.email,
        }).create();
      } catch (e: any) {
        await new MailEventLog({
          userID: user._id,
          mailType: MailEventLogType.WORKSHOP_ATTENDANCE_REGISTER,
          successful: false,
          sendMailError: e.message,
          emailAddress: user.email,
        }).create();
      }
    };
  },
  options: {},
  params: [
    { type: () => String, name: 'appointmentID' },
    { type: () => String, name: 'userID' },
  ],
});
